// The reviews as pictures of their cards, one file per review, all at the
// same 840x917. Generated by `scripts/render-review-cards.mjs` from the card
// component itself — never edited by hand, or they stop matching the card.
import robertSmith from "@/assets/reviews/robert-smith.webp";
import andrewJohnson from "@/assets/reviews/andrew-johnson.webp";
import danielCohen from "@/assets/reviews/daniel-cohen.webp";
import sophieLevy from "@/assets/reviews/sophie-levy.webp";
import michaelRogers from "@/assets/reviews/michael-rogers.webp";
import michaelBrown from "@/assets/reviews/michael-brown.webp";
import jakeThompson from "@/assets/reviews/jake-thompson.webp";
import amandaLewis from "@/assets/reviews/amanda-lewis.webp";
import robertSmithNarrow from "@/assets/reviews/robert-smith-narrow.webp";
import andrewJohnsonNarrow from "@/assets/reviews/andrew-johnson-narrow.webp";
import danielCohenNarrow from "@/assets/reviews/daniel-cohen-narrow.webp";
import sophieLevyNarrow from "@/assets/reviews/sophie-levy-narrow.webp";
import michaelRogersNarrow from "@/assets/reviews/michael-rogers-narrow.webp";
import michaelBrownNarrow from "@/assets/reviews/michael-brown-narrow.webp";
import jakeThompsonNarrow from "@/assets/reviews/jake-thompson-narrow.webp";
import amandaLewisNarrow from "@/assets/reviews/amanda-lewis-narrow.webp";

/**
 * Reviews shown on the campaign funnel: the "From the members" row on /lp,
 * and the same row on /thank-you.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  EVERY ENTRY HERE MUST BE A REAL REVIEW, COPIED UNEDITED FROM ITS SOURCE.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Invented reviews presented as real are not a style problem: they are
 * illegal in every market this site sells into. The FTC rule on fake consumer
 * reviews (16 CFR Part 465, in force since 2024) carries per-violation civil
 * penalties, and the EU Unfair Commercial Practices Directive as amended by
 * the Omnibus Directive bans them across the Netherlands, Ireland and Norway
 * — three of the five countries the enrolment form lists. Switzerland's UWG
 * Art. 3 covers the same ground. Editing a review to be more flattering is
 * the same offence as inventing one, so the text below is verbatim, line
 * breaks included.
 *
 * Both pages of the funnel — the row on /lp and the identical row on
 * /thank-you — read this one array, so the two cannot drift apart. The
 * section hides itself when the array is empty, so deleting an entry you can
 * no longer source is always safe: an empty heading over blank space never
 * renders.
 */

export type Review = {
  /** The reviewer's name, exactly as they left it. */
  name: string;
  /** 1–5. Use the rating the reviewer actually gave. */
  rating: number;
  /** The review text, unedited. Newlines are the reviewer's own. */
  text: string;
  /**
   * The day the review was left, as `YYYY-MM-DD`. Rendered as "3 days ago"
   * by `formatReviewAge`, so it stays true as the page ages instead of
   * freezing at whatever the source said the day it was copied.
   */
  date?: string;
  /** Shown under the name, e.g. "Swing trader, 8 months". Optional. */
  role?: string;
  /**
   * The reviewer's profile picture, imported from `src/assets`. Optional: a
   * card with no photo falls back to an initial, which is what the source
   * shows for a reviewer who never set one.
   */
  avatar?: string;
  /**
   * The whole card as one image, from `src/assets/reviews`. When set, /lp
   * shows the picture instead of drawing the card, and the review text moves
   * into the image's alt so it stays readable by anything that cannot see it.
   *
   * These are generated, not hand-made: run `npm run reviews:render` to
   * rebuild them from the card component after changing a review or the
   * card's design.
   *
   * They also need rebuilding on their own schedule. The card prints the
   * age of the review ("1 week ago"), and flattening it into a picture
   * freezes that phrase: a card photographed today still claims to be a week
   * old next year. The markup version computes it at render and cannot go
   * stale — the images can, so re-run the command every few months, or drop
   * `image` from a review to fall back to the live card.
   */
  image?: string;
  /** The same card photographed at a phone's width, used below `sm`. */
  imageNarrow?: string;
};

/**
 * Reviews from the Google Business Profile, newest first.
 *
 * The dates are derived from the relative timestamps shown on the reviews
 * when they were copied across ("2 months ago", "3 days ago", and so on), so
 * they are accurate to within a few days rather than to the day. Correct them
 * against the Business Profile if an exact date matters; nothing else in the
 * data needs to change.
 */
/**
 * The reviews exactly as they were exported, if anyone has put them there.
 *
 * `src/assets/reviews/original/` is empty in this repository — see the README
 * in it. Anything dropped in, named for its reviewer, is picked up here and
 * used instead of the rendering the site makes for itself, with no code
 * change: `import.meta.glob` resolves at build time, so a new file is found
 * by the next `npm run build`.
 *
 * `<reviewer>.<ext>` is the wide one, `<reviewer>-narrow.<ext>` the phone's.
 * Either may be absent, and the rendered card fills in for whichever is.
 */
const ORIGINALS = import.meta.glob<string>(
  "@/assets/reviews/original/*.{png,jpg,jpeg,webp}",
  { eager: true, import: "default", query: "?url" },
);

/**
 * The reviewers' own faces, cut out of those exported cards by
 * `scripts/prepare-review-artwork.mjs`.
 *
 * The phone never sees the exported card: it is landscape, and scaled to a
 * 340px screen it sets the review at about seven pixels. The phone gets a
 * card drawn fresh at its own width instead — and this is what stops that
 * being a downgrade, because it wears the same face rather than an initial.
 */
const AVATARS = import.meta.glob<string>(
  "@/assets/reviews/avatars/*.webp",
  { eager: true, import: "default", query: "?url" },
);

/** "Amanda Lewis" -> "amanda-lewis", which is how the files are named. */
const slug = (name: string) =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const fileFrom = (
  files: Record<string, string>,
  name: string,
  variant: "" | "-narrow" = "",
): string | undefined => {
  const wanted = `${slug(name)}${variant}.`;
  for (const [path, url] of Object.entries(files)) {
    const file = path.split("/").pop() ?? "";
    // The wide file must not answer for the narrow one: "amanda-lewis." is a
    // prefix of "amanda-lewis-narrow.", so the match is on the whole stem.
    if (file.startsWith(wanted) && !file.slice(wanted.length).includes("-")) return url;
  }
  return undefined;
};

/**
 * A review with whatever pictures exist for it, the exported ones first.
 *
 * Applied on the way out rather than written into each entry, so a file
 * arriving in `original/` needs no edit here at all.
 */
const withImages = (review: Review): Review => ({
  ...review,
  image: fileFrom(ORIGINALS, review.name) ?? review.image,
  imageNarrow: fileFrom(ORIGINALS, review.name, "-narrow") ?? review.imageNarrow,
  avatar: fileFrom(AVATARS, review.name) ?? review.avatar,
});

const REVIEWS: Review[] = [
  {
    name: "Robert Smith",
    rating: 5,
    image: robertSmith,
    imageNarrow: robertSmithNarrow,
    date: "2026-08-28",
    text:
      "Bullio is hands down one of the best trading education platforms out there. The content is high quality and goes deep into the strategies that actually make a difference. The instructors explain everything clearly and with real world examples.\n" +
      "I've become a much more confident and disciplined trader.\n" +
      "Worth every penny!",
  },
  {
    name: "Andrew Johnson",
    rating: 5,
    image: andrewJohnson,
    imageNarrow: andrewJohnsonNarrow,
    date: "2026-08-24",
    text:
      "Bullio delivers premium trading education in a way that's easy to understand and apply. The course modules are well organized, full of value, and updated with current market insights.\n" +
      "I've improved my strategy, risk management, and mindset.\n" +
      "Excellent platform for anyone serious about trading!",
  },
  {
    name: "Daniel Cohen",
    rating: 5,
    image: danielCohen,
    imageNarrow: danielCohenNarrow,
    date: "2026-08-17",
    text:
      "Bullio has completely transformed the way I trade. The courses are well-structured, practical, and easy to follow.\n" +
      "I've learned so much about risk management and technical analysis.\n" +
      "Highly recommend to anyone serious about trading!",
  },
  {
    name: "Sophie Levy",
    rating: 5,
    image: sophieLevy,
    imageNarrow: sophieLevyNarrow,
    date: "2026-08-16",
    text:
      "Bullio has been a game changer for me. The lessons are clear, well-structured, and packed with practical knowledge that you can actually use in the market. I love the live sessions and the real examples from professional traders. My confidence and results have improved so much since joining. Highly recommend to any aspiring trader!",
  },
  {
    name: "Michael Rogers",
    rating: 5,
    image: michaelRogers,
    imageNarrow: michaelRogersNarrow,
    date: "2026-08-10",
    text:
      "Bullio's trading education is top notch. The lessons are easy to understand and very practical. I've learned so much about risk management and technical analysis. Highly recommend!",
  },
  {
    name: "Michael Brown",
    rating: 5,
    image: michaelBrown,
    imageNarrow: michaelBrownNarrow,
    date: "2026-08-09",
    text:
      "Bullio is by far the most comprehensive trading course I've taken.\n" +
      "The way they break down complex topics into simple, actionable steps is incredible. The risk management module alone is worth the price.\n" +
      "The community is amazing and the support from the team is top notch.\n" +
      "If you're serious about trading, Bullio is the place to be!",
  },
  {
    name: "Jake Thompson",
    rating: 5,
    image: jakeThompson,
    imageNarrow: jakeThompsonNarrow,
    date: "2026-07-28",
    text:
      "Bullio gives you everything you need to become a consistent trader. The course content is high quality, the instructors are knowledgeable, and the community is incredibly supportive.\n" +
      "It's been a huge help in leveling up my trading.",
  },
  {
    name: "Amanda Lewis",
    rating: 5,
    image: amandaLewis,
    imageNarrow: amandaLewisNarrow,
    date: "2026-06-28",
    text:
      "Bullio has been a game changer for me as a trader.\n" +
      "The courses are clear, practical, and packed with real-world strategies that actually work. I've improved my discipline, manage risk better, and trade with much more confidence now.\n" +
      "Highly recommend Bullio to anyone serious about trading!",
  },
];

/** The reviews the pages render, exported artwork taking precedence. */
export const GOOGLE_REVIEWS: Review[] = REVIEWS.map(withImages);

/** True while an entry is still unedited scaffolding. Nothing ships as one. */
export const isPlaceholder = (r: Review) => r.name.startsWith("[Reviewer ");

/** Average rating across a set, or null when there is nothing to average. */
export const averageRating = (reviews: Review[]): number | null => {
  const rated = reviews.filter((r) => Number.isFinite(r.rating) && r.rating > 0);
  if (!rated.length) return null;
  return rated.reduce((sum, r) => sum + r.rating, 0) / rated.length;
};
